import { notification } from 'antd'
import { datadogRum } from '@datadog/browser-rum'

/**
 * Default error message shown to users
 */
export const DEFAULT_ERROR_MESSAGE =
  'An error occurred. Refresh the page and try again. If the issue persists, please contact support.'

/**
 * Maximum number of array items to include in error reports
 */
const MAX_ARRAY_ITEMS = 5

/**
 * Maximum depth for object traversal in error reports
 */
const MAX_DEPTH = 3

/**
 * Sanitizes data for error reporting by limiting array sizes and object depth
 *
 * @param {*} data - The data to sanitize
 * @param {number} depth - Current traversal depth (internal use)
 * @returns {*} - Sanitized data safe for error reporting
 */
export const sanitizeErrorData = (data, depth = 0) => {
  // Return simple types as-is
  if (data === null || data === undefined || typeof data !== 'object') {
    return data
  }

  // Limit recursion depth
  if (depth >= MAX_DEPTH) {
    return Array.isArray(data) ? '[Array]' : '[Object]'
  }

  // Handle arrays - sample only a few items
  if (Array.isArray(data)) {
    const length = data.length

    if (length <= MAX_ARRAY_ITEMS) {
      return data.map((item) => sanitizeErrorData(item, depth + 1))
    }

    // Take a sample from beginning, middle and end
    const sample = [
      ...data.slice(0, 2).map((item) => sanitizeErrorData(item, depth + 1)),
      `... ${length - 4} more items ...`,
      ...data.slice(length - 2).map((item) => sanitizeErrorData(item, depth + 1)),
    ]

    return sample
  }

  // Handle objects
  const result = {}

  // Limit the number of keys
  const keys = Object.keys(data)
  const processKeys =
    keys.length > MAX_ARRAY_ITEMS ? [...keys.slice(0, 3), '...', ...keys.slice(keys.length - 2)] : keys

  processKeys.forEach((key) => {
    if (key === '...') {
      result[key] = `${keys.length - 5} more properties`
    } else {
      result[key] = sanitizeErrorData(data[key], depth + 1)
    }
  })

  return result
}

/**
 * Tracks an error in Datadog RUM and displays a notification
 *
 * @param {*} error - The error object or message
 * @param {Object} options - Additional tracking options
 * @param {string} options.source - The source of the error (e.g., 'feeManagement.overrideSheets')
 * @param {string} options.message - Optional custom message to display in the notification
 * @param {Object} options.context - Additional context to include with the error
 * @param {Function} options.onClose - Callback when notification is closed
 * @returns {void}
 */
export const trackAndNotifyError = (error, options = {}) => {
  const { source = 'feeManagement.unknown', message = DEFAULT_ERROR_MESSAGE, context = {}, onClose } = options

  // Sanitize context data before sending to Datadog
  const sanitizedContext = sanitizeErrorData(context)

  // Track in Datadog
  datadogRum.addError(error, {
    source,
    ...sanitizedContext,
  })

  // Log for debugging
  console.error(`[${source}] Error:`, error)

  // Notify user
  notification.error({
    message: 'Error',
    description: message,
    duration: 8,
    onClose: onClose,
  })
}

/**
 * Creates an error handler that can be used in catch blocks
 *
 * @param {Object} options - Options for the error handler
 * @param {string} options.source - Source identifier for the error
 * @param {string} options.message - Optional custom message
 * @param {Object} options.context - Additional context
 * @returns {Function} - Error handler function
 */
export const createErrorHandler = (options = {}) => {
  return (error) => {
    trackAndNotifyError(error, options)
    return error
  }
}

/**
 * Higher-order function that wraps an async function with error handling
 *
 * @param {Function} fn - The async function to wrap
 * @param {Object} options - Error handling options
 * @returns {Function} - Wrapped function with error handling
 */
export const withErrorHandling = (fn, options = {}) => {
  return async (...args) => {
    try {
      return await fn(...args)
    } catch (error) {
      trackAndNotifyError(error, options)
      throw error // Re-throw to allow the caller to handle it
    }
  }
}
